<template>
  <v-app class="login-app">
    <LoginSnackbar :snackbar="snackbar" />

    <div :class="pageClass">
      <LoginBackground />

      <div class="login-page__brand" aria-label="Yandle">
        <LoginBrandLogo
          :is-dark="isDark"
          :org-logo-url="logoPreviewUrl"
          :title="loginTitle"
          :variant="logoPreviewUrl ? 'org' : 'full'"
        />
      </div>

      <LoginThemeToggle
        :is-dark="isDark"
        @set-light="setLight"
        @set-dark="setDark"
      />

      <div class="login-page__shell">
        <div class="login-page__hero-col">
          <LoginHeroSection
            :is-dark="isDark"
            :logo-url="logoPreviewUrl"
            :title="loginTitle"
          />
        </div>

        <div class="login-page__panel-col">
          <div class="login-page__panel-inner">
            <LoginCard
              ref="loginCardRef"
              :is-dark="isDark"
              :logo-url="logoPreviewUrl"
              :title="loginTitle"
              :email="email"
              :email-rules="emailRules"
              :otp-digits="otpDigits"
              :otp-sent="otpSent"
              :masked-email="maskedEmail()"
              :timer-display="timerDisplay"
              :timer-ring-offset="timerRingOffset"
              :timer-urgent="timerUrgent"
              :is-otp-complete="isOtpComplete"
              :form-disabled="loginBlocked || loginCompleting"
              :loading="isLoading"
              :resend-disabled="counter > 0"
              :landing-loading="landingOrgLoading"
              :landing-error="landingOrgError"
              @update:email="email = $event"
              @send-otp="onSendOtp"
              @verify-otp="onVerifyOtp"
              @change-email="onChangeEmail"
              @otp-input="onOtpInput"
              @otp-keydown="onOtpKeydown"
              @otp-paste="onOtpPaste"
              @register-otp-refs="otpRefs = $event"
            />
          </div>
        </div>
      </div>
    </div>
  </v-app>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import LoginSnackbar from '@/components/Login/LoginSnackbar.vue'
import LoginBackground from '@/components/Login/LoginBackground.vue'
import LoginThemeToggle from '@/components/Login/LoginThemeToggle.vue'
import LoginBrandLogo from '@/components/Login/LoginBrandLogo.vue'
import LoginHeroSection from '@/components/Login/LoginHeroSection.vue'
import LoginCard from '@/components/Login/LoginCard.vue'
import { useLoginTheme } from '@/composables/login/useLoginTheme'
import { useLoginLandingOrg } from '@/composables/login/useLoginLandingOrg'
import { useOtpInput } from '@/composables/login/useOtpInput'
import { useLoginAuth } from '@/composables/login/useLoginAuth'

defineOptions({ name: 'LoginPage' })

const snackbar = ref({})
const loginCardRef = ref(null)
const otpRefs = ref({})

const formRef = computed(() => loginCardRef.value?.formRef ?? null)

const { pageClass, isDark, setLight, setDark } = useLoginTheme()

const {
  logoPreviewUrl,
  landingOrgLoading,
  landingOrgError,
  loginTitle,
  loginBlocked,
} = useLoginLandingOrg()

const otpControls = useOtpInput()
const {
  otpDigits,
  otpSent,
  isOtpComplete,
  counter,
  timerDisplay,
  timerRingOffset,
  timerUrgent,
  focusOtpInput,
  handleOTPInput,
  handleOTPPaste,
  handleOTPKeydown,
} = otpControls

function scheduleOtpFocus() {
  nextTick(() => {
    window.setTimeout(() => focusOtpInput(0, otpRefs.value), 320)
  })
}

const {
  email,
  isLoading,
  loginCompleting,
  emailRules,
  loginBusy,
  maskedEmail,
  sendOtpRequest,
  verifyOtpRequest,
  resetForm,
} = useLoginAuth({
  otpControls,
  formRef,
  onOtpReady: scheduleOtpFocus,
})

watch(otpSent, (sent) => {
  if (sent) scheduleOtpFocus()
})

function showSnack(color, text, options = {}) {
  snackbar.value = {
    SnackbarVmodel: true,
    SnackbarColor: color,
    SnackbarText: text,
    SnackbarTitle: options.title || '',
    SnackbarSubtitle: options.subtitle || '',
  }
}

function showAuthSnack(result) {
  if (!result?.message && !result?.snackbar) return

  if (result.snackbar) {
    showSnack(result.type || 'success', '', {
      title: result.snackbar.title,
      subtitle: result.snackbar.subtitle,
    })
    return
  }

  showSnack(result.type || 'error', result.message)
}

async function onSendOtp() {
  if (!email.value) {
    showSnack('error', 'Please enter a valid email to sign in')
    return
  }
  const result = await sendOtpRequest()
  showAuthSnack(result)
}

async function onVerifyOtp() {
  if (!isOtpComplete.value) {
    showSnack('error', 'Please enter 6-digit OTP')
    return
  }
  const result = await verifyOtpRequest()
  showAuthSnack(result)
}

async function onChangeEmail() {
  await resetForm()
}

function onOtpInput(index, event) {
  handleOTPInput(index, event, otpRefs.value, loginBusy())
}

function onOtpKeydown(index, event) {
  handleOTPKeydown(index, event, otpRefs.value, loginBusy())
}

function onOtpPaste(index, event) {
  handleOTPPaste(index, event, otpRefs.value, loginBusy())
}
</script>
